// =====================================================================
// src/core/env.ts
//
// Validación runtime de variables de entorno + export tipado.
//
// Política:
//   - TODAS las variables VITE_* se validan aquí al bootstrap.
//   - Si falta alguna, se lanza error CON instrucciones de fix.
//   - El resto del código consume `env.foo` directamente con tipos
//     refinados (string, no string | undefined).
//
// Cómo agregar una variable nueva:
//   1. Agregar al .env.example y .env.local con docs.
//   2. Agregar al tipo Env de este archivo.
//   3. Validar en parseEnv() abajo.
//   4. Exportar via el objeto `env`.
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

// ─── TIPOS ──────────────────────────────────────────────────────────

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';
export type AppEnvironment = 'development' | 'staging' | 'production';

interface FeatureFlags {
  /** Página de Actividad (log de auditoría) habilitada. */
  readonly actividad: boolean;
  /** Invitación de miembros vía Edge Function team-invite habilitada. */
  readonly teamInvite: boolean;
}

interface SupabaseConfig {
  readonly url: string;
  readonly anonKey: string;
  readonly functionsUrl: string;
}

export interface Env {
  readonly supabase: SupabaseConfig;
  readonly app: {
    readonly url: string;
    readonly version: string;
    readonly buildTime: string;
    readonly environment: AppEnvironment;
  };
  readonly logLevel: LogLevel;
  readonly features: FeatureFlags;
}

// ─── HELPERS DE PARSEO Y VALIDACIÓN ──────────────────────────────────

/**
 * Lanza error con mensaje accionable si la variable no está definida.
 * El mensaje incluye dónde configurarla según el contexto (local vs Vercel).
 */
const assertEnv = (key: string, value: string | undefined): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    const isProduction = import.meta.env.MODE === 'production';
    const location = isProduction
      ? 'Vercel Dashboard → Project Settings → Environment Variables'
      : '.env.local en la raíz del repo';
    throw new Error(
      `[env] Variable de entorno requerida no definida: ${key}\n` +
        `Configurala en: ${location}\n` +
        `Para más info, ver .env.example`,
    );
  }
  return value.trim();
};

/**
 * Parsea string de feature flag a boolean. Acepta "true"/"false" (case
 * insensitive). Cualquier otro valor se considera false (fail-safe).
 */
const parseBoolean = (value: string | undefined): boolean => {
  return value?.toLowerCase().trim() === 'true';
};

/**
 * Parsea LogLevel con fallback seguro. Si el valor no es válido,
 * retorna 'info' (nivel default razonable).
 */
const parseLogLevel = (value: string | undefined): LogLevel => {
  const validLevels: readonly LogLevel[] = ['debug', 'info', 'warn', 'error', 'silent'];
  const normalized = value?.toLowerCase().trim();
  return validLevels.find((level) => level === normalized) ?? 'info';
};

/**
 * Parsea AppEnvironment con fallback. Si no es válido, retorna 'production'
 * (fail-safe: prefiere prod-mode estricto a dev-mode permisivo).
 */
const parseEnvironment = (value: string | undefined): AppEnvironment => {
  const validEnvs: readonly AppEnvironment[] = ['development', 'staging', 'production'];
  const normalized = value?.toLowerCase().trim();
  return validEnvs.find((env_) => env_ === normalized) ?? 'production';
};

// ─── PARSEO PRINCIPAL ────────────────────────────────────────────────

const parseEnv = (): Env => {
  // Acceso directo a import.meta.env (Vite lo inyecta en compile time).
  const raw = import.meta.env;

  // Variables CRÍTICAS: si faltan, no podemos arrancar.
  const supabaseUrl = assertEnv('VITE_SUPABASE_URL', raw.VITE_SUPABASE_URL);
  const supabaseAnonKey = assertEnv('VITE_SUPABASE_ANON_KEY', raw.VITE_SUPABASE_ANON_KEY);
  const supabaseFunctionsUrl = assertEnv(
    'VITE_SUPABASE_FUNCTIONS_URL',
    raw.VITE_SUPABASE_FUNCTIONS_URL,
  );
  const appUrl = assertEnv('VITE_APP_URL', raw.VITE_APP_URL);

  // Variables OPCIONALES con fallbacks seguros.
  const logLevel = parseLogLevel(raw.VITE_LOG_LEVEL);
  const environment = parseEnvironment(raw.VITE_ENV);

  // Feature flags: default false (fail-safe para features experimentales).
  // Las features actuales tienen default true porque ya están listas.
  const featureActividad = parseBoolean(raw.VITE_FEATURE_ACTIVIDAD);
  const featureTeamInvite = parseBoolean(raw.VITE_FEATURE_TEAM_INVITE);

  return {
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
      functionsUrl: supabaseFunctionsUrl,
    },
    app: {
      url: appUrl,
      version: __APP_VERSION__,
      buildTime: __BUILD_TIME__,
      environment,
    },
    logLevel,
    features: {
      actividad: featureActividad,
      teamInvite: featureTeamInvite,
    },
  };
};

// ─── EXPORT (frozen para inmutabilidad) ──────────────────────────────

/**
 * Variables de entorno validadas y tipadas del proyecto.
 *
 * Si alguna variable crítica falta al bootstrap, este módulo arroja
 * Error inmediatamente. El bootstrap del app debe atrapar este error
 * y mostrar pantalla de "configuración inválida" al usuario.
 *
 * @example
 *   import { env } from '@/core/env';
 *   const client = createClient(env.supabase.url, env.supabase.anonKey);
 */
export const env: Env = Object.freeze(parseEnv());

// ─── HELPERS PÚBLICOS ────────────────────────────────────────────────

/**
 * ¿Estamos en producción? Útil para logging condicional.
 */
export const isProduction = (): boolean => env.app.environment === 'production';

/**
 * ¿Estamos en desarrollo local?
 */
export const isDevelopment = (): boolean => env.app.environment === 'development';

/**
 * ¿Una feature flag está habilitada?
 *
 * @example
 *   if (isFeatureEnabled('teamInvite')) {
 *     showInviteButton();
 *   }
 */
export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => env.features[feature];
